import type {AssetADto} from '@cohort/admin-schemas/asset';
import type {CampaignADto, PatchCampaignADto} from '@cohort/admin-schemas/campaign';
import {
  isDefaultLanguageTranslationConsistent,
  isDefaultTranslationDefined,
  removeEmptyLanguages,
  removeUndefinedLanguages,
} from '@cohort/merchants/lib/form/localization';
import {fileValidatorOptional, hexColorValidator} from '@cohort/merchants/lib/form/validators';
import {CampaignRewardStepSchema} from '@cohort/merchants/pages/campaigns/campaign/edit/CampaignRewardStep';
import {CampaignTypeStepSchema} from '@cohort/merchants/pages/campaigns/campaign/edit/CampaignTypeStep';
import {
  LanguageSchema,
  LocalizedRichTextSchema,
  LocalizedStringSchema,
} from '@cohort/shared/schema/common';
import type {AssetKind} from '@cohort/shared/schema/common/assets';
import {
  CampaignAudienceSchema,
  CampaignPricingSchema,
  CampaignTypeSchema,
  LocalizedFaqsSchema,
} from '@cohort/shared/schema/common/campaign';
import type {Price} from '@cohort/shared/schema/common/currency';
import {CurrencySchema, CurrencySpecs, PricesSchema} from '@cohort/shared/schema/common/currency';
import {isVideoFile} from '@cohort/shared/utils/mimeTypes';
import {isFile, isFileList} from '@cohort/shared-frontend/utils/isFile';
import type {UseMutateAsyncFunction} from '@tanstack/react-query';
import {isEmpty} from 'remeda';
import {z} from 'zod';

export const CampaignAudienceStepSchema = z
  .object({
    audience: CampaignAudienceSchema,
    cohortId: z.string().uuid().nullable(),
  })
  .superRefine(({audience, cohortId}, ctx) => {
    if (audience === 'cohort' && cohortId === null) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorRequired',
        path: ['cohortId'],
      });
    }
  });

export type CampaignAudienceStepValues = z.infer<typeof CampaignAudienceStepSchema>;

const DefaultLanguageSchema = LanguageSchema.optional();
const DefinedLanguagesSchema = z.array(LanguageSchema).optional();

// We need a dummy price schema for the preprocessing as they wouln't be validated.
const PriceSchema = z.object({
  amount: z.number(),
  currency: CurrencySchema,
});

function formPricesToDto(arg: unknown): Price[] {
  const prices = z.array(PriceSchema).parse(arg);
  const processedPrices = [];
  for (const price of prices) {
    const parsedAmount = CurrencySpecs.fromDecimal(price.currency, price.amount);
    processedPrices.push({
      currency: price.currency,
      amount: parsedAmount,
    });
  }
  return processedPrices;
}

// Base store campaigns data schema.
export const BaseCampaignStoreSettingsStepSchema = z.object({
  store: z.object({
    // Computed field, need to be optional for automatic step validation
    descriptionSource: z.enum(['digital-asset', 'store']).optional(),
    description: LocalizedRichTextSchema,
    // Computed field, need to be optional for automatic step validation
    storeThemeColorType: z.enum(['experience-space', 'digital-asset', 'custom']).optional(),
    backgroundColorCode: z
      .string()
      .min(1, {message: 'errorRequired'})
      .refine(hexColorValidator, 'errorHexColor'),
    accentColorCode: z
      .string()
      .min(1, {message: 'errorRequired'})
      .refine(hexColorValidator, 'errorHexColor'),
    pricing: CampaignPricingSchema,
    defaultCurrency: CurrencySchema.nullable(),
    prices: z.preprocess(formPricesToDto, PricesSchema),
  }),
  defaultLanguage: DefaultLanguageSchema,
  definedLanguages: DefinedLanguagesSchema,
});
type BaseCampaignStoreSettingsStep = z.infer<typeof BaseCampaignStoreSettingsStepSchema>;

// Base journeys campaigns data schema.
export const BaseCampaignJourneySettingsStepSchema = z.object({
  journey: z.object({
    description: LocalizedRichTextSchema,
    title: LocalizedStringSchema,
    optInCta: LocalizedStringSchema,
    imageFileKey: z.any().refine(fileValidatorOptional),
    animationFileKey: z.any().refine(fileValidatorOptional),
  }),
  defaultLanguage: DefaultLanguageSchema,
  definedLanguages: DefinedLanguagesSchema,
});
type BaseCampaignJourneySettingsStep = z.infer<typeof BaseCampaignJourneySettingsStepSchema>;

// Base airdrop campaigns data schema.
export const BaseCampaignAirdropSettingsStepSchema = z.object({airdrop: z.object({})});

// Custom campaign journeys refinement for "Save for later button".
export const SaveJourneyForLaterRefinement = (
  request: Partial<BaseCampaignJourneySettingsStep>,
  ctx: z.RefinementCtx
): {message: string; path: string[]} | boolean => {
  const {journey, defaultLanguage, definedLanguages} = request;

  if (!journey || !defaultLanguage || !definedLanguages) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'errorRequired',
      path: ['journey'],
    });
    return false;
  }

  // If the data in default language is not defined but it is defined in another language, we need to throw an error.
  if (definedLanguages.length < 2) {
    return true;
  } else {
    if (!isDefaultLanguageTranslationConsistent(journey.title, defaultLanguage)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorTooShort3',
        path: ['journey.title'],
      });
      return false;
    }

    if (!isDefaultLanguageTranslationConsistent(journey.description, defaultLanguage)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorTooShort3',
        path: ['journey.description'],
      });
      return false;
    }

    if (!isDefaultLanguageTranslationConsistent(journey.optInCta, defaultLanguage)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorTooShort3',
        path: ['journey.optInCta'],
      });
      return false;
    }
  }

  return true;
};

// Custom campaign store refinement for "Save for later" button.
export const SaveStoreForLaterRefinement = (
  request: Partial<BaseCampaignStoreSettingsStep>,
  ctx: z.RefinementCtx
): {message: string; path: string[]} | boolean => {
  const {store, defaultLanguage, definedLanguages} = request;

  if (!store || !defaultLanguage || !definedLanguages) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'errorRequired',
      path: ['store'],
    });
    return false;
  }

  // If the data in default language is not defined but it is defined in another language, we need to throw an error.
  if (definedLanguages.length < 2) {
    return true;
  } else {
    if (
      store.descriptionSource === 'store' &&
      !isDefaultLanguageTranslationConsistent(store.description, defaultLanguage)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorTooShort3',
        path: ['store.description'],
      });
      return false;
    }
  }

  return true;
};

// Refined store campaigns data schema.
export const RefinedCampaignStoreSettingsStepSchema =
  BaseCampaignStoreSettingsStepSchema.superRefine(({store, defaultLanguage}, ctx) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (store === undefined || defaultLanguage === undefined) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorRequired',
        path: ['store'],
      });
    }

    if (
      store.descriptionSource === 'store' &&
      !isDefaultTranslationDefined(store.description, defaultLanguage)
    ) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorTooShort3',
        path: ['store.description'],
      });
    }

    if (store.storeThemeColorType === 'custom') {
      if (isEmpty(store.backgroundColorCode)) {
        return ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'errorRequired',
          path: ['store.backgroundColorCode'],
        });
      }

      if (isEmpty(store.accentColorCode)) {
        return ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'errorRequired',
          path: ['store.accentColorCode'],
        });
      }
    }
  }).transform(data => {
    data.store.description = removeUndefinedLanguages(
      data.store.description,
      data.definedLanguages
    );
    return data;
  });

// Refined journeys campaigns data schema.
export const RefinedCampaignJourneySettingsStepSchema =
  BaseCampaignJourneySettingsStepSchema.superRefine(({defaultLanguage, journey}, ctx) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (journey === undefined || defaultLanguage === undefined) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorRequired',
        path: ['journey'],
      });
    }

    if (!isDefaultTranslationDefined(journey.description, defaultLanguage)) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorTooShort3',
        path: ['journey.description'],
      });
    }

    if (!isDefaultTranslationDefined(journey.title, defaultLanguage)) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorTooShort3',
        path: ['journey.title'],
      });
    }

    if (!isDefaultTranslationDefined(journey.optInCta, defaultLanguage)) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorTooShort3',
        path: ['journey.optInCta'],
      });
    }
  }).transform(data => {
    data.journey.description = removeUndefinedLanguages(
      data.journey.description,
      data.definedLanguages
    );
    data.journey.title = removeUndefinedLanguages(data.journey.title, data.definedLanguages);
    data.journey.optInCta = removeUndefinedLanguages(data.journey.optInCta, data.definedLanguages);
    return data;
  });

// Refined airdrop campaigns data schema.
export const RefinedCampaignAirdropSettingsStepSchema = BaseCampaignAirdropSettingsStepSchema;

// Base campaigns data schema (store & journeys).
const BaseCampaignSettingsStepSchema = z
  .object({
    campaignType: CampaignTypeSchema.nullable(),
    // Computed field, need to be optional for automatic step validation
    withMaxSupply: z.boolean().default(true),
    totalSupply: z.number().nullable(),
    tosFileKey: z.any().refine(fileValidatorOptional),
    faqs: LocalizedFaqsSchema,
    // Computed field, need to be optional for automatic step validation
    digitalAssetDescription: LocalizedStringSchema.optional(),
    // Computed field, need to be optional for automatic step validation
    defaultLanguage: LanguageSchema.optional(),
    selectedLanguage: LanguageSchema.optional(),
    definedLanguages: z.array(LanguageSchema).optional(),
  })
  .merge(BaseCampaignAirdropSettingsStepSchema)
  .merge(BaseCampaignStoreSettingsStepSchema)
  .merge(BaseCampaignJourneySettingsStepSchema)
  .partial({
    airdrop: true,
    journey: true,
    store: true,
  });

export const CampaignSettingsStepSchema = BaseCampaignSettingsStepSchema.superRefine(
  ({campaignType, defaultLanguage}, ctx) => {
    if (campaignType === null) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorRequired',
        path: ['campaignType'],
      });
    }

    if (defaultLanguage === undefined) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'errorRequired',
        path: ['defaultLanguage'],
      });
    }
  }
).transform(data => {
  data.faqs = removeEmptyLanguages(data.faqs);
  data.faqs = removeUndefinedLanguages(data.faqs, data.definedLanguages);
  return data;
});

export type CampaignSettingsStepValues = z.infer<typeof CampaignSettingsStepSchema>;

export const validateAndUpload = async (
  formData: Partial<CampaignSettingsStepValues>,
  uploadMutation: UseMutateAsyncFunction<
    AssetADto | null,
    unknown,
    {file: File; assetKind: AssetKind},
    unknown
  >
): Promise<PatchCampaignADto> => {
  const updatedData = {
    ...formData,
    totalSupply: formData.withMaxSupply ? formData.totalSupply : null,
  } satisfies PatchCampaignADto;

  // TosFileKey
  if (formData.tosFileKey && isFile(formData.tosFileKey)) {
    const asset = await uploadMutation({
      file: formData.tosFileKey,
      assetKind: 'termsAndConditions',
    });

    if (asset) {
      updatedData.tosFileKey = asset.fileKey;
    }
  } else if (
    (!formData.tosFileKey && !isFile(formData.tosFileKey)) ||
    // This happens when canceling the file upload.
    (isFileList(formData.tosFileKey) && formData.tosFileKey.length === 0)
  ) {
    updatedData.tosFileKey = null; // Nullify if the file has been removed by the merchant.
  }

  // Upload Journey assets.
  if (updatedData.journey !== undefined && formData.journey !== undefined) {
    if (formData.journey.imageFileKey && isFile(formData.journey.imageFileKey)) {
      const asset = await uploadMutation({
        file: formData.journey.imageFileKey,
        assetKind: 'journeyVisual',
      });

      if (asset) {
        updatedData.journey.animationFileKey = isVideoFile(asset.mimeType) ? asset.fileKey : null;
        updatedData.journey.imageFileKey = isVideoFile(asset.mimeType) ? null : asset.fileKey;
      }
    } else if (!updatedData.journey.imageFileKey || updatedData.journey.imageFileKey.length === 0) {
      updatedData.journey.imageFileKey = null;
      updatedData.journey.animationFileKey = null;
    }
  }

  // Faq
  if (updatedData.faqs !== undefined && updatedData.definedLanguages !== undefined) {
    updatedData.faqs = removeEmptyLanguages(updatedData.faqs);
    updatedData.faqs = removeUndefinedLanguages(updatedData.faqs, updatedData.definedLanguages);
  }

  return updatedData;
};

export const CampaignFormSteps = ['type', 'audience', 'reward', 'settings', 'review'] as const;

export type CampaignFormStep = (typeof CampaignFormSteps)[number];

type CampaignFormStepsValidators = Record<
  CampaignFormStep,
  z.ZodObject<z.ZodRawShape> | z.ZodEffects<z.ZodObject<z.ZodRawShape>>
>;

export const campaignFormStepsValidators: CampaignFormStepsValidators = {
  type: CampaignTypeStepSchema,
  audience: CampaignAudienceStepSchema,
  reward: CampaignRewardStepSchema,
  settings: BaseCampaignSettingsStepSchema,
  review: z.object({
    foo: z.string(),
  }),
};

export type FormStepProps = {
  isProcessing: boolean;
  setStepDirty: (dirty: boolean) => void;
  setStepSaveBtn: (btn: JSX.Element) => void;
  onStepValidated: () => void;
  updateCampaign: UseMutateAsyncFunction<CampaignADto | null, unknown, PatchCampaignADto, unknown>;
};

export type FormStepPropsWithUpload = {
  uploadMutation: UseMutateAsyncFunction<
    AssetADto | null,
    unknown,
    {file: File; assetKind: AssetKind},
    unknown
  >;
};
